import React, { useEffect, useState } from 'react';
import type { Language } from '../types';
import { getTranslation } from '../utils/translations';
import XIcon from './icons/XIcon';
import {
    canPromptInstall,
    isAppInstalled,
    isIOS,
    subscribeInstallAvailability,
    triggerInstallPrompt,
} from '../utils/pwaInstall';

interface InstallAppSettingProps {
    language: Language;
}

const DownloadIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 11l5 5 5-5M12 4v12" />
    </svg>
);

const ShareIosIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 16V4m0 0L8 8m4-4l4 4M6 12v6a2 2 0 002 2h8a2 2 0 002-2v-6" />
    </svg>
);

const InstallAppSetting: React.FC<InstallAppSettingProps> = ({ language }) => {
    const t = (key: string) => getTranslation(language, key);
    const [installed, setInstalled] = useState(false);
    const [canInstall, setCanInstall] = useState(false);
    const [showGuide, setShowGuide] = useState(false);

    useEffect(() => {
        setInstalled(isAppInstalled());
        setCanInstall(canPromptInstall());
        return subscribeInstallAvailability(() => {
            setInstalled(isAppInstalled());
            setCanInstall(canPromptInstall());
        });
    }, []);

    const handleClick = async () => {
        if (canInstall) {
            const outcome = await triggerInstallPrompt();
            if (outcome === 'accepted') setInstalled(true);
            setCanInstall(canPromptInstall());
            return;
        }
        setShowGuide(true);
    };

    const onIOS = isIOS();
    const guideSteps = onIOS
        ? [t('installAppIOSStep1'), t('installAppIOSStep2'), t('installAppIOSStep3')]
        : [t('installAppHintGeneric')];

    return (
        <div className="pt-4 border-t border-slate-100 dark:border-slate-800">
            <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2 uppercase tracking-wide">
                {t('installApp')}
            </label>

            {installed ? (
                <div className="space-y-3">
                    <div className="flex items-center gap-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-900/30 rounded-2xl p-3">
                        <div className="w-9 h-9 rounded-xl bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 flex items-center justify-center flex-shrink-0">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <p className="text-sm font-bold text-emerald-700 dark:text-emerald-300">
                            {t('installAppInstalledLabel')}
                        </p>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed px-1">
                        {t('installAppShareHint')}
                    </p>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={handleClick}
                    className="w-full flex items-center gap-3 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-2xl p-3 hover:border-brand-sage hover:bg-brand-sage/5 transition-all text-start"
                >
                    <div className="w-9 h-9 rounded-xl bg-brand-sage/15 text-brand-sage flex items-center justify-center flex-shrink-0">
                        <DownloadIcon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-black text-slate-800 dark:text-slate-100">{t('installAppMenuItem')}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 leading-snug mt-0.5">{t('installAppMenuDesc')}</p>
                    </div>
                </button>
            )}

            {showGuide && (
                <div
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
                    onClick={() => setShowGuide(false)}
                >
                    <div
                        className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-sm border dark:border-slate-800 overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="p-5 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center">
                            <div className="flex items-center gap-2 text-slate-800 dark:text-slate-100">
                                <span className="w-8 h-8 rounded-lg bg-brand-sage/15 text-brand-sage flex items-center justify-center">
                                    {onIOS ? <ShareIosIcon className="w-4 h-4" /> : <DownloadIcon className="w-4 h-4" />}
                                </span>
                                <h3 className="text-base font-black">{t('installAppGuideTitle')}</h3>
                            </div>
                            <button
                                onClick={() => setShowGuide(false)}
                                className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                                aria-label={t('close')}
                            >
                                <XIcon className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-5">
                            <ol className="space-y-3">
                                {guideSteps.map((step, index) => (
                                    <li key={index} className="flex gap-3 items-start">
                                        <span className="w-6 h-6 rounded-full bg-brand-sage text-white text-xs font-black flex items-center justify-center flex-shrink-0">
                                            {index + 1}
                                        </span>
                                        <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed pt-0.5">{step}</p>
                                    </li>
                                ))}
                            </ol>
                        </div>
                        <div className="p-5 pt-0">
                            <button
                                type="button"
                                onClick={() => setShowGuide(false)}
                                className="w-full py-2.5 bg-brand-sage text-white rounded-xl font-black uppercase tracking-widest text-sm shadow-lg shadow-brand-sage/20 hover:opacity-90 transition-all"
                            >
                                {t('gotIt')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InstallAppSetting;
